import { ClassChangeTip } from "@/components/race/ClassChangeTip";
const tone = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-slate-400");
const fmtDate = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const HEAD = ["No.", "Horse", "LS Day / Date", "LS Venue", "Track Type", "Age", "Table Used", "RA Code", "Class Matched", "LS Score", "This Race", "Class Score"];

const Step = ({ label, value, testId, accent }) => (
  <div data-testid={testId}>
    <div className="eyebrow">{label}</div>
    <div className={`text-sm mt-0.5 ${accent || "text-white"}`}>{value || "—"}</div>
  </div>
);

const ThisRace = ({ race }) => {
  const cs = race.class_score || {};
  return (
    <div className="bg-[#0F172A] border border-slate-800 rounded p-4" data-testid="class-detail-this-race">
      <div className="eyebrow text-emerald-400 mb-3">Step 1 · This race</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
        <Step label="Venue" value={race.venue} />
        <Step label="Date" value={`${cs.day || ""} ${fmtDate(race.date)}`} />
        <Step label="Track Type" value={cs.category} />
        <Step label="Public Holiday" value={cs.holiday || "No"} />
        <Step label="Age" value={cs.age} />
        <Step label="Table Used" value={cs.table} />
        <Step label="Class Matched" value={cs.class_label} testId="class-detail-this-class" />
        <Step label="Class Score" value={cs.score} testId="class-detail-this-score" accent="text-emerald-400 font-bold text-lg num" />
      </div>
    </div>
  );
};

export const ClassRuleDetail = ({ race, ratings }) => {
  const byName = Object.fromEntries((race.horses || []).map((h) => [h.name, h]));
  const thisScore = race.class_score?.score;
  const rows = [...ratings].sort((a, b) => b.scores.class_change.weighted - a.scores.class_change.weighted);
  return (
    <div className="space-y-5" data-testid="class-rule-detail">
      <ThisRace race={race} />
      <div>
        <div className="eyebrow text-amber-400 mb-2">Step 2 · Each horse's last official start, scored with the same rules → Step 3 · Class Score = LS Score − This Race</div>
        <div className="scroll-x">
          <table className="w-full text-sm" data-testid="class-detail-table">
            <thead>
              <tr className="eyebrow border-b border-slate-800 text-left">
                {HEAD.map((h, i) => <th key={h} className={`px-2 py-2.5 font-normal whitespace-nowrap ${i >= 9 ? "text-right" : ""}`}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const ls = byName[r.name]?.last_start;
                const cs = ls?.class_score || {};
                const score = r.scores.class_change;
                return (
                  <tr key={r.name} className="row-hover border-b border-slate-800/60" data-testid={`class-detail-row-${i}`}>
                    <td className="px-2 py-2.5 num text-emerald-400 font-bold">{r.number ?? "-"}</td>
                    <td className="px-2 py-2.5 text-white font-medium whitespace-nowrap">{r.name}</td>
                    {ls ? (<>
                      <td className="px-2 py-2.5 whitespace-nowrap">{cs.day} <span className="num text-xs text-slate-500">{fmtDate(ls.date)}</span>{cs.holiday && <span className="block text-[10px] text-amber-400">Public holiday: {cs.holiday}</span>}</td>
                      <td className="px-2 py-2.5 whitespace-nowrap">{ls.venue} <span className="text-[10px] text-slate-500 num">{ls.venue_code}</span></td>
                      <td className="px-2 py-2.5">{cs.category}</td>
                      <td className="px-2 py-2.5 text-xs whitespace-nowrap">{cs.age}</td>
                      <td className="px-2 py-2.5 whitespace-nowrap">{cs.table}</td>
                      <td className="px-2 py-2.5 num text-xs text-slate-400">{ls.class_raw}</td>
                      <td className="px-2 py-2.5 whitespace-nowrap" data-testid={`class-detail-ls-class-${i}`}>{cs.class_label}</td>
                      <td className="px-2 py-2.5 num text-right" data-testid={`class-detail-ls-score-${i}`}>{cs.score ?? "—"}</td>
                    </>) : <td colSpan={8} className="px-2 py-2.5 text-xs text-slate-600">No official last start</td>}
                    <td className="px-2 py-2.5 num text-right text-slate-400">{thisScore ?? "—"}</td>
                    <td className={`px-2 py-2.5 num text-right font-bold ${tone(score.weighted)}`} data-testid={`class-detail-score-${i}`}>
                      <ClassChangeTip today={thisScore} ls={ls} testId={`class-detail-tip-${i}`}>{score.value == null ? "0" : `${score.weighted > 0 ? "+" : ""}${score.weighted}`}</ClassChangeTip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-3">Positive = dropping in class today, negative = rising in class. A race with no class or benchmark uses the Open column. Track lists and tables: Rules &amp; Matrices → Class Scores.</p>
      </div>
    </div>
  );
};
